/**
 * The signed-in doctor's dental plan templates and the favourite per condition.
 *
 * Favourites must apply offline, so the last list is cached in localStorage,
 * keyed by clinic and doctor so a shared clinic device never applies one
 * doctor's favourites to another doctor's patients. Templates hold procedure
 * codes and the doctor's own labels only; no patient data.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ConditionKey } from "../constants/dental.constants";
import { DoctorTemplateAPI, type DoctorTemplate, type DoctorTemplateCreate } from "../services/doctor-template.service";
import type { Favourites } from "../utils/dental-plan";

const cacheKey = (clinicId: string, doctorId: string) => `kgz_dental_templates:${clinicId}:${doctorId}`;

function readCache(key: string): DoctorTemplate[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as DoctorTemplate[]) : [];
  } catch {
    return [];
  }
}

function writeCache(key: string, rows: DoctorTemplate[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(rows));
  } catch {
    /* storage unavailable: favourites still work for this page load */
  }
}

export function favouritesFrom(rows: DoctorTemplate[]): Favourites {
  const out: Favourites = {};
  for (const r of rows) {
    if (r.is_favourite && r.condition_key) out[r.condition_key as ConditionKey] = r.payload.items;
  }
  return out;
}

/** Pass enabled=false (not a dentist, or the feature is off) to make no call. */
export function useDentalTemplates(enabled: boolean, clinicId: string | null, doctorId: string) {
  const key = clinicId && doctorId ? cacheKey(clinicId, doctorId) : null;
  const [templates, setTemplates] = useState<DoctorTemplate[]>(() => (enabled && key ? readCache(key) : []));

  const refresh = useCallback(async () => {
    if (!enabled || !key) return;
    try {
      const rows = await DoctorTemplateAPI.list();
      setTemplates(rows);
      writeCache(key, rows);
    } catch {
      // Offline or refused: keep the cached favourites.
      setTemplates(readCache(key));
    }
  }, [enabled, key]);

  useEffect(() => { void refresh(); }, [refresh]);

  const save = useCallback(async (body: DoctorTemplateCreate) => {
    const created = await DoctorTemplateAPI.create(body);
    await refresh();
    return created;
  }, [refresh]);

  const favourites = useMemo(() => favouritesFrom(templates), [templates]);
  const favouriteNames = useMemo(() => {
    const out: Partial<Record<ConditionKey, string>> = {};
    for (const r of templates) if (r.is_favourite && r.condition_key) out[r.condition_key as ConditionKey] = r.name;
    return out;
  }, [templates]);

  return { templates, favourites, favouriteNames, save, refresh };
}
